import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';
import type { SupportedLanguage } from '../locales';
import { supportedLanguages, languageNames, defaultLanguage } from '../locales';

/**
 * Hook to get the current locale and a function to change it.
 *
 * @example
 * ```tsx
 * function LanguagePicker() {
 *   const { currentLanguage, changeLanguage, languages } = useLocale();
 *
 *   return (
 *     <select value={currentLanguage} onChange={(e) => changeLanguage(e.target.value)}>
 *       {languages.map(({ code, name }) => (
 *         <option key={code} value={code}>{name}</option>
 *       ))}
 *     </select>
 *   );
 * }
 * ```
 */
export function useLocale() {
  const { i18n } = useTranslation();

  const currentLanguage = i18n.language as SupportedLanguage;

  const changeLanguage = useCallback(
    async (language: SupportedLanguage) => {
      // LanguageDetector handles localStorage persistence automatically via cache config
      await i18n.changeLanguage(language);
    },
    [i18n]
  );

  /**
   * Resets to device locale by clearing stored preference.
   * On web, this clears localStorage and uses browser detection.
   * On React Native, hooks.native.ts provides the real implementation.
   */
  const setAutoLanguage = useCallback(async () => {
    // On web, clear localStorage and re-detect
    try {
      localStorage.removeItem('vizi-language');
    } catch {
      // localStorage may not be available in some environments
    }
    // Re-detect using browser's language
    const browserLang = navigator.language.split('-')[0];
    const detectedLang = supportedLanguages.includes(browserLang as SupportedLanguage)
      ? (browserLang as SupportedLanguage)
      : defaultLanguage;
    await i18n.changeLanguage(detectedLang);
  }, [i18n]);

  /**
   * Gets the device's detected locale.
   * On web, returns browser's detected language.
   * On React Native, hooks.native.ts provides the real implementation.
   */
  const deviceLocale: SupportedLanguage = (() => {
    try {
      const browserLang = navigator.language.split('-')[0];
      return supportedLanguages.includes(browserLang as SupportedLanguage)
        ? (browserLang as SupportedLanguage)
        : defaultLanguage;
    } catch {
      return defaultLanguage;
    }
  })();

  const languages = supportedLanguages.map((code) => ({
    code,
    name: languageNames[code],
  }));

  return {
    currentLanguage,
    changeLanguage,
    languages,
    supportedLanguages,
    languageNames,
    // Native-compatible properties (web implementations)
    deviceLocale,
    setAutoLanguage,
  };
}

/**
 * Hook to translate strings in the 'common' namespace.
 * For other namespaces, use `useTranslation` directly from react-i18next.
 *
 * @example
 * ```tsx
 * function LoadingButton() {
 *   const t = useCommonTranslation();
 *   return <button>{t('loading')}</button>;
 * }
 * ```
 */
export function useCommonTranslation() {
  const { t } = useTranslation('common');
  return t;
}

/**
 * Hook to translate strings in the 'admin' namespace.
 *
 * @example
 * ```tsx
 * function Dashboard() {
 *   const t = useAdminTranslation();
 *   return <h1>{t('dashboard.title')}</h1>;
 * }
 * ```
 */
export function useAdminTranslation() {
  const { t } = useTranslation('admin');
  return t;
}

/**
 * Hook to translate strings in the 'native' namespace.
 *
 * @example
 * ```tsx
 * function HomeScreen() {
 *   const t = useNativeTranslation();
 *   return <Text>{t('home.title')}</Text>;
 * }
 * ```
 */
export function useNativeTranslation() {
  const { t } = useTranslation('native');
  return t;
}

/**
 * Hook to translate error strings.
 *
 * @example
 * ```tsx
 * function ErrorMessage({ code }: { code: string }) {
 *   const t = useErrorTranslation();
 *   return <span>{t(code)}</span>;
 * }
 * ```
 */
export function useErrorTranslation() {
  const { t } = useTranslation('errors');
  return t;
}

// Re-export useTranslation for flexibility
export { useTranslation } from 'react-i18next';
